
import React, { useEffect, useRef } from 'react';
import { Message, ToolApproval } from '../../../types';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { Icons } from '../../../components/ui/Icons';
import { ActionCardWrapper } from '../../../components/ui/ActionCard';

interface ChatContainerProps {
  messages: Message[];
  inputValue: string;
  onInputChange: (val: string) => void;
  onSend: (msg: string, file?: File, interventionId?: string, interventionAnswer?: any) => void;
  onCancel?: () => void;
  isProcessing: boolean;
  onApproveAction?: (messageId: string, approval: ToolApproval, updatedArgs?: any) => void;
  onRejectAction?: (messageId: string, approval: ToolApproval) => void;
  onOpenIntegrations?: () => void;
  onOpenPricing?: () => void;
  onOpenSchedules?: (prefill?: string) => void;
  isScheduleMode?: boolean;
  onToggleScheduleMode?: () => void;
  currentSessionId: string;
  streamingThinking?: string;
  streamingResponse?: string;
  processingStatus?: 'thinking' | 'executing' | 'analyzing' | '';
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  inputValue,
  onInputChange,
  onSend,
  onCancel,
  isProcessing,
  onApproveAction,
  onRejectAction,
  onOpenIntegrations,
  onOpenPricing,
  onOpenSchedules,
  isScheduleMode,
  onToggleScheduleMode,
  currentSessionId,
  streamingThinking,
  streamingResponse,
  processingStatus
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingThinking, streamingResponse, processingStatus]);

  // Show the inline shimmer for initial thinking (before any text appears)
  const showThinkingShimmer = isProcessing && processingStatus === 'thinking' && !streamingResponse;
  
  // Show streaming response text in a live message bubble
  const showStreamingResponse = isProcessing && processingStatus === 'thinking' && !!streamingResponse;
  
  // Show dot-ball loading for executing and analyzing (between tool steps)
  const showDotLoading = isProcessing && (processingStatus === 'executing' || processingStatus === 'analyzing');

  return (
    <div className="flex flex-col h-full relative">
      {/* Top Gradient Fade */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white dark:from-[#050505] to-transparent z-10 pointer-events-none" />

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 md:px-0 py-8 scroll-smooth no-scrollbar">
        <div className="max-w-3xl mx-auto min-h-full flex flex-col justify-end pb-4">
          {messages.map((msg, idx) => {
            const isNonUser = msg.type === 'ai' || msg.type === 'system';
            const prevMsg = idx > 0 ? messages[idx - 1] : null;
            const isPrevNonUser = !!(prevMsg && (prevMsg.type === 'ai' || prevMsg.type === 'system'));
            
            // Group follow-ups together (hide avatar) if the previous message was also non-user
            const isContinuation = isNonUser && isPrevNonUser;
            
            // Determine if this is the last message in an AI's response turn
            const nextMsg = idx < messages.length - 1 ? messages[idx + 1] : null;
            const isNextUser = !!(nextMsg && nextMsg.type === 'user');
            const isEndOfTurn = isNonUser && (!nextMsg || isNextUser);

            // Find the most recent toolApproval in this turn (from current message back to the start of the turn)
            let turnToolApproval: ToolApproval | undefined = undefined;
            if (isEndOfTurn) {
              for (let i = idx; i >= 0; i--) {
                if (messages[i].type === 'user') {
                  break; // stop when we hit the user message that started this turn
                }
                if (messages[i].toolApproval) {
                  turnToolApproval = messages[i].toolApproval;
                  break;
                }
              }
            }

            return (
              <ChatMessage 
                  key={msg.id} 
                  message={msg} 
                  onApproveAction={onApproveAction}
                  onRejectAction={onRejectAction}
                  onSendMessage={onSend}
                  onOpenPricing={onOpenPricing}
                  onOpenIntegrations={onOpenIntegrations}
                  onOpenSchedules={onOpenSchedules}
                  currentSessionId={currentSessionId}
                  isLast={idx === messages.length - 1}
                  isEndOfTurn={isEndOfTurn}
                  turnToolApproval={turnToolApproval}
                  hideAvatar={isContinuation}
              />
            );
          })}

          {/* ─── Thinking Shimmer ─── */}
          {/* Subtle pulsing dots while waiting for the first response token */}
          {showThinkingShimmer && (
              <div className="flex w-full mb-4 justify-start md:pl-12 animate-in fade-in slide-in-from-bottom-2 duration-300 mt-4">
                  <div className="flex items-center gap-3 py-2 px-1">
                      {/* Subtle thinking label */}
                      <div className="flex items-center gap-2">
                          <div className="relative flex items-center justify-center w-6 h-6">
                              <div className="absolute inset-0 rounded-lg bg-violet-50 dark:bg-violet-950/30" />
                              <svg className="w-3.5 h-3.5 text-violet-400 relative animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                              </svg>
                          </div>
                          <span className="text-[12px] font-medium text-black dark:text-white">
                              {streamingThinking 
                                ? <span className="text-black dark:text-white italic max-w-[280px] truncate inline-block align-bottom">{streamingThinking}</span>
                                : 'Thinking'
                              }
                          </span>
                          <div className="flex gap-[3px] items-center">
                              <div className="w-1 h-1 bg-gray-300 dark:bg-gray-700 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <div className="w-1 h-1 bg-gray-300 dark:bg-gray-700 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <div className="w-1 h-1 bg-gray-300 dark:bg-gray-700 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                      </div>
                  </div>
              </div>
          )}

          {/* ─── Streaming Response Bubble ─── */}
          {/* Live text appearing word-by-word in a real message bubble */}
          {showStreamingResponse && streamingResponse && (
              <div className="flex w-full mb-8 justify-start animate-in fade-in duration-200">
                  <div className="max-w-[90%] md:max-w-[85%] flex gap-4 flex-row w-full">
                      <div className="flex-shrink-0 mt-1 hidden md:block">
                          <div className="w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 shadow-sm">
                              <Icons.Logo className="w-5 h-5" />
                          </div>
                      </div>
                      <div className="flex flex-col items-start w-full">
                          <div className="text-[15px] break-words w-full text-[#202124] dark:text-[#ececec] bg-white dark:bg-transparent max-w-[720px] md:min-w-[720px] mx-auto py-2">
                                  <div className="py-1">
                                      {streamingResponse.split('\n').map((line, idx, arr) => {
                                          const trimmed = line.trim();
                                          if (!trimmed) return <div key={idx} className="h-2" />;
                                          const isLastLine = idx === arr.length - 1;
                                          return (
                                              <p key={idx} className="text-[15px] text-[#202124] dark:text-[#ececec] leading-relaxed mb-2">
                                                  {trimmed}
                                                  {isLastLine && (
                                                      <span className="inline-block w-[2px] h-[1.1em] bg-[#202124] dark:bg-[#ececec] ml-0.5 align-text-bottom animate-blink" />
                                                  )}
                                              </p>
                                          );
                                      })}
                                  </div>
                                  {/* Show thought as a tiny toggle while streaming */}
                                  {streamingThinking && (
                                      <div className="mt-2">
                                          <span className="text-[11px] text-black dark:text-gray-400 italic flex items-center gap-1">
                                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400/60" />
                                              {streamingThinking.length > 60 ? streamingThinking.slice(0, 60) + '...' : streamingThinking}
                                          </span>
                                      </div>
                                  )}
                          </div>
                      </div>
                  </div>
              </div>
          )}

          {/* Dot-ball loading — shown between steps (after execution completes, before next thinking) */}
          {showDotLoading && (
            <div className="flex w-full mb-4 justify-start md:pl-12">
              <div className="flex items-center gap-2 py-3 px-2">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-gray-300 dark:bg-gray-750 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-300 dark:bg-gray-750 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-300 dark:bg-gray-750 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bottom Input Area */}
      <div className="flex-none bg-white/80 dark:bg-[#050505]/80 backdrop-blur-md pb-6 pt-4 px-4 md:px-0 z-20">
        <div className="max-w-3xl mx-auto">
            <ChatInput 
                value={inputValue}
                onChange={onInputChange}
                onSend={onSend}
                onCancel={onCancel}
                disabled={isProcessing}
                onOpenIntegrations={onOpenIntegrations}
                onOpenSchedules={onOpenSchedules}
                isScheduleMode={isScheduleMode}
                onToggleScheduleMode={onToggleScheduleMode}
                variant="chat"
            />
            <div className="text-center mt-3">
                <span className="text-[10px] text-black dark:text-gray-400 uppercase tracking-widest font-semibold">
                    NEURA AI v1.0 • NEURA CAN MAKE MISTAKES
                </span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;


